"use client";

import React, { useEffect, useState } from "react";
import { Mail, Phone, ArrowRight, Loader, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const nicheContent = {
  default: {
    title: "Partner with Experts",
    subtitle: "Fractional IT services designed for agile startups. Flat $110/device pricing including RMM, support, and zero-markup O365 licensing support.",
    phone: "$110 / device",
    email: "partner@smbit.tech",
    badge: "Startup Focused"
  },
  clinica: {
    title: "Scale Your Support",
    subtitle: "We understand the rapid pace of high-growth teams. Get end-to-end device management and Windows updates without the enterprise overhead.",
    phone: "Flat-Rate IT",
    email: "growth@smbit.tech",
    badge: "Fractional IT"
  },
  estetica: {
    title: "Technical Excellence",
    subtitle: "Premium infrastructure management for modern startups. Secure your fleet with our dedicated RMM tools and expert end-user support.",
    phone: "Expert Support",
    email: "ops@smbit.tech",
    badge: "Modern Infrastructure"
  }
};
type NicheKey = keyof typeof nicheContent;
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const inputBaseClasses = "w-full bg-transparent border-0 border-b border-border/50 px-0 py-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent focus:bg-primary/5 focus:px-4 focus:rounded-md transition-all duration-300 ease-out font-mono";
  if (isSubmitted && !submitError) {
    return <div data-section-id="224611" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <Sparkles className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Success!</h3>
        <p className="text-muted-foreground font-sans">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-medium font-sans">Company Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Startup Inc." className={inputBaseClasses} />
              </FormControl>
              <FormDescription className="font-sans">Your legal entity or brand name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="email" rules={{
          required: "Work email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-medium font-sans">Work Email</FormLabel>
                <FormControl>
                  <input {...field} type="email" placeholder="founder@startup.com" className={inputBaseClasses} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Contact number is required",
          pattern: {
            value: /^\(?\d{2}\)?[\s-]?\d{4,5}-?\d{4}$/,
            message: "Invalid format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-medium font-sans">Contact Number</FormLabel>
                <FormControl>
                  <input {...field} type="tel" placeholder="(11) 98765-4321" className={inputBaseClasses} />
                </FormControl>
                <FormDescription className="font-sans">Direct line for technical audit</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-medium font-sans">Device Count & Details</FormLabel>
              <FormControl>
                <textarea {...field} placeholder="How many devices do you need to support? Any specific O365 requirements?" rows={3} className={`${inputBaseClasses} resize-none`} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-semibold bg-gradient-to-r from-primary to-chart-4 text-primary-foreground hover:opacity-90 transition-all shadow-xl shadow-primary/20 hover:shadow-primary/40 rounded-xl group">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Analyzing...
          </> : <>
            Schedule Your Audit
            <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
export default function ConexaoImediata() {
  const [niche, setNiche] = useState<NicheKey>("default");
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const n = params.get("nicho");
      if (n === "clinica" || n === "estetica") {
        setNiche(n as NicheKey);
      }
    }
  }, []);
  const content = nicheContent[niche];
  return <section id="partner-with-experts" className="bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-chart-1/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={leftRef} className={`space-y-8 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="inline-flex items-center rounded-full border border-border/50 bg-card/30 backdrop-blur-sm px-3 py-1 text-sm font-medium text-primary shadow-sm font-mono">
              <Sparkles className="mr-2 size-4" />
              {content.badge}
            </div>
            
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tighter text-foreground">
                {content.title}
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl font-sans">
                {content.subtitle}
              </p>
            </div>

            <div className="pt-8 space-y-6 border-t border-border/30">
              <div className="flex items-center group">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-card border border-border/50 shadow-sm group-hover:border-primary/50 group-hover:bg-primary/5 transition-colors">
                  <Phone className="size-5 text-primary" />
                </div>
                <div className="ml-4">
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground font-sans">Flat Pricing</p>
                  <p className="text-lg font-semibold text-foreground font-mono">{content.phone}</p>
                </div>
              </div>
              
              <div className="flex items-center group">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-card border border-border/50 shadow-sm group-hover:border-primary/50 group-hover:bg-primary/5 transition-colors">
                  <Mail className="size-5 text-primary" />
                </div>
                <div className="ml-4">
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground font-sans">Direct Channel</p>
                  <p className="text-lg font-semibold text-foreground font-mono">Nesta@smbit.tech</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative">
              {}
              <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-b from-primary/30 to-transparent pointer-events-none" />
              
              <div className="relative bg-card/40 backdrop-blur-2xl border border-border/50 rounded-3xl p-8 md:p-10 shadow-2xl shadow-black/40">
                <div className="mb-8">
                  <h3 className="text-2xl font-default font-semibold text-foreground">
                    Get Started
                  </h3>
                  <p className="text-muted-foreground mt-2 font-sans">
                    Submit your details and we&apos;ll build your custom IT roadmap.
                  </p>
                </div>

                <FormProvider formKey="partner_experts_form_home" formId="partner_experts_form_home" sectionName="conexao-imediata" defaultValues={{
                name: "",
                email: "",
                phone: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                }
              }} submitText="Schedule Your Audit" successMessage="Thank you! Our technical team will reach out within 2 hours to schedule your audit." errorMessage="An error occurred while processing your request. Please try again.">
                  <ContactForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
      <Toaster position="bottom-right" />
    </section>;
}